import React, { useState, useEffect, useRef } from 'react';
import { 
  Search, 
  Filter, 
  Download, 
  Eye, 
  Ticket, 
  Users, 
  Calendar, 
  MapPin, 
  QrCode, 
  X,
  CheckCircle,
  Clock,
  DollarSign,
  Printer,
  Share2,
  Smartphone,
  User,
  CreditCard,
  AlertCircle,
  ChevronDown,
  BarChart3,
  FileText,
  RefreshCw,
  Mail,
  Phone
} from 'lucide-react';
import { eventsService } from '../services/api';
import html2canvas from 'html2canvas';

const AdminTicketManager = ({ eventId, eventDetails }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    ticket_type: ''
  });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [generatingTickets, setGeneratingTickets] = useState(false);
  const [ticketCount, setTicketCount] = useState(100);
  const [ticketDesign, setTicketDesign] = useState('standard');
  const [includeSeating, setIncludeSeating] = useState(false);
  
  // Confirmation modal states
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [pendingTicketCode, setPendingTicketCode] = useState(null);
  
  const ticketRef = useRef(null);

  useEffect(() => {
    if (eventId) {
      fetchTickets();
      fetchStats();
    }
  }, [eventId, filters]);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const data = await eventsService.getEventTickets(eventId);
      setTickets(data || []);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await eventsService.getTicketStats(eventId);
      setStats(data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const exportTickets = async (format = 'csv') => {
    try {
      setExporting(true);
      
      if (format === 'csv') {
        // Export as CSV
        const csvContent = convertToCSV(tickets);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `tickets_${eventId}_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else if (format === 'pdf') {
        // Export as PDF (simulated - you'd need a PDF library)
        alert('PDF export feature coming soon!');
      }
      
    } catch (error) {
      console.error('Error exporting tickets:', error);
      alert('Failed to export tickets');
    } finally {
      setExporting(false);
    }
  };

  const convertToCSV = (tickets) => {
    const headers = ['Ticket Code', 'Holder Name', 'Holder Email', 'Status', 'Ticket Type', 'Price', 'Purchase Date', 'Seat', 'Section', 'Gate'];
    const rows = tickets.map(ticket => [
      ticket.ticket_code,
      ticket.ticket_holder_name || '',
      ticket.ticket_holder_email || '',
      ticket.status,
      ticket.ticket_type || 'General',
      ticket.price_paid || '0',
      ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : '',
      ticket.seat_number || '',
      ticket.section || '',
      ticket.gate || ''
    ]);
    
    return [headers, ...rows].map(row => 
      row.map(cell => `"${cell}"`).join(',')
    ).join('\n');
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setShowTicketModal(true);
  };

  const handleRedeemTicket = async (ticketCode) => {
    try {
      await eventsService.redeemTicket(eventId, ticketCode);
      alert('Ticket redeemed successfully!');
      fetchTickets();
      fetchStats();
    } catch (error) {
      alert(error.message || 'Error redeeming ticket');
    }
  };

  const handleRedeemConfirmation = (ticketCode) => {
    setConfirmMessage('Are you sure you want to redeem this ticket? This action cannot be undone.');
    setPendingAction('redeemSingle');
    setPendingTicketCode(ticketCode);
    setShowConfirmModal(true);
  };

  const handleGenerateTickets = async () => {
    if (!ticketCount || ticketCount < 1) {
        alert('Please enter a valid number of tickets');
        return;
    }

    setGeneratingTickets(true);
    try {
        const response = await eventsService.generateVisualTickets(eventId, {
            quantity: ticketCount,
            ticket_design: ticketDesign,
            include_seating: includeSeating
        });
        
        if (response.success) {
            alert(`${response.ticket_count} tickets generated successfully!`);
            console.log('Generated tickets:', response.ticket_codes);
        } else {
            alert(response.error || 'Error generating tickets');
        }
        
        setTicketCount(100);
        setTicketDesign('standard');
        setIncludeSeating(false);
        fetchTickets(); // Refresh the ticket list
        fetchStats();   // Refresh statistics
        
    } catch (error) {
        console.error('Ticket generation error:', error);
        alert(error.message || 'Error generating tickets');
    } finally {
        setGeneratingTickets(false);
    }
};

  const handleSelectTicket = (ticketId) => {
    setSelectedTickets(prev => 
      prev.includes(ticketId) 
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const handleSelectAll = () => {
    if (selectedTickets.length === tickets.length) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(tickets.map(t => t.id));
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedTickets.length === 0) {
      alert('Please select tickets first');
      return;
    }

    switch (action) {
      case 'redeem':
        setConfirmMessage(`Redeem ${selectedTickets.length} selected tickets? This action cannot be undone.`);
        setPendingAction('redeemBulk');
        setShowConfirmModal(true);
        break;
      
      case 'export':
        const selectedTicketData = tickets.filter(t => selectedTickets.includes(t.id));
        const csvContent = convertToCSV(selectedTicketData);
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `selected_tickets_${eventId}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        break;
      
      case 'print':
        // Print selected tickets
        alert(`Printing ${selectedTickets.length} tickets...`);
        break;
    }
  };

  const executePendingAction = async () => {
    try {
      switch (pendingAction) {
        case 'redeemBulk':
          await Promise.all(
            selectedTickets.map(id => 
              eventsService.redeemTicket(eventId, tickets.find(t => t.id === id)?.ticket_code)
            )
          );
          alert('Tickets redeemed successfully!');
          setSelectedTickets([]);
          break;
        
        case 'redeemSingle':
          if (pendingTicketCode) {
            await eventsService.redeemTicket(eventId, pendingTicketCode);
            alert('Ticket redeemed successfully!');
          }
          break;
      }
      
      fetchTickets();
      fetchStats();
    } catch (error) {
      alert(error.message || 'Error performing action');
    } finally {
      setShowConfirmModal(false);
      setPendingAction(null);
      setConfirmMessage('');
      setPendingTicketCode(null);
    }
  };

  const downloadTicketImage = async (ticket) => {
    try {
      const canvas = await html2canvas(ticketRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        logging: false
      });
      
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `ticket_${ticket.ticket_code}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading ticket:', err);
      alert('Failed to download ticket image');
    }
  };

  const TicketStatusBadge = ({ status }) => {
    const statusConfig = {
      purchased: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      redeemed: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle },
      reserved: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      cancelled: { color: 'bg-red-100 text-red-800', icon: X }
    };
    
    const config = statusConfig[status] || statusConfig.reserved;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const filteredTickets = tickets.filter(ticket => {
    if (filters.search && !ticket.ticket_code.toLowerCase().includes(filters.search.toLowerCase()) && 
        !ticket.ticket_holder_name?.toLowerCase().includes(filters.search.toLowerCase())) {
      return false;
    }
    
    if (filters.status && ticket.status !== filters.status) {
      return false;
    }
    
    if (filters.ticket_type && (!ticket.ticket_type || ticket.ticket_type !== filters.ticket_type)) {
      return false;
    }
    
    return true;
  });

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Ticket className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.total_tickets || 0}</div>
                <div className="text-sm text-gray-500">Total Tickets</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Users className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.purchased_tickets || 0}</div>
                <div className="text-sm text-gray-500">Purchased</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">{stats.redeemed_tickets || 0}</div>
                <div className="text-sm text-gray-500">Redeemed</div>
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900">₵{stats.revenue?.toFixed(2) || '0.00'}</div>
                <div className="text-sm text-gray-500">Revenue</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controls Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search tickets..."
                  value={filters.search}
                  onChange={(e) => setFilters({...filters, search: e.target.value})}
                  className="pl-10 w-full rounded-lg border border-gray-300 px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff9702] focus:border-[#ff9702]"
                />
              </div>
              
              <div className="flex items-center gap-3">
                <select
                  value={filters.status}
                  onChange={(e) => setFilters({...filters, status: e.target.value})}
                  className="rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff9702] focus:border-[#ff9702]"
                >
                  <option value="">All Status</option>
                  <option value="reserved">Reserved</option>
                  <option value="purchased">Purchased</option>
                  <option value="redeemed">Redeemed</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                
                <select
                  value={filters.ticket_type}
                  onChange={(e) => setFilters({...filters, ticket_type: e.target.value})}
                  className="rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff9702] focus:border-[#ff9702]"
                >
                  <option value="">All Types</option>
                  <option value="General">General</option>
                  <option value="VIP">VIP</option>
                  <option value="Premium">Premium</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowBulkActions(!showBulkActions)}
              className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <span>Bulk Actions</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${showBulkActions ? 'rotate-180' : ''}`} />
            </button>
            
            <div className="relative">
              <button
                onClick={() => exportTickets('csv')}
                disabled={exporting}
                className="flex items-center space-x-2 bg-[#ff9702] text-white px-4 py-2 rounded-lg hover:bg-[#d18c13] transition-colors disabled:opacity-50"
              >
                {exporting ? (
                  <RefreshCw className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                <span>Export</span>
              </button>
            </div>
          </div>
        </div>
        
        {/* Bulk Actions Panel */}
        {showBulkActions && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={selectedTickets.length === tickets.length && tickets.length > 0}
                  onChange={handleSelectAll}
                  className="h-4 w-4 text-[#ff9702] rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  {selectedTickets.length} tickets selected
                </span>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleBulkAction('redeem')}
                  disabled={selectedTickets.length === 0}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Mark as Redeemed
                </button>
                <button
                  onClick={() => handleBulkAction('export')}
                  disabled={selectedTickets.length === 0}
                  className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Export Selected
                </button>
                <button
                  onClick={() => handleBulkAction('print')}
                  disabled={selectedTickets.length === 0}
                  className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Print Selected
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Generate Tickets Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Generate New Tickets</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Number of Tickets</label>
            <input
              type="number"
              min="1"
              value={ticketCount}
              onChange={(e) => setTicketCount(parseInt(e.target.value) || 0)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff9702] focus:border-[#ff9702]"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ticket Design</label>
            <select
              value={ticketDesign}
              onChange={(e) => setTicketDesign(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#ff9702] focus:border-[#ff9702]"
            >
              <option value="standard">Standard</option>
              <option value="vip">VIP</option>
              <option value="premium">Premium</option>
              <option value="custom">Custom</option>
            </select>
          </div>
          
          <div className="flex items-end">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={includeSeating}
                onChange={(e) => setIncludeSeating(e.target.checked)}
                className="h-4 w-4 text-[#ff9702] rounded"
              />
              <span className="text-sm text-gray-700">Include seating info</span>
            </label>
          </div>
          
          <div className="flex items-end">
            <button
              onClick={handleGenerateTickets}
              disabled={generatingTickets}
              className="w-full bg-[#1d1d1d] text-white px-4 py-2.5 rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              {generatingTickets ? (
                <>
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <Ticket className="h-4 w-4" />
                  <span>Generate Tickets</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="w-12 px-6 py-3">
                  <input
                    type="checkbox"
                    checked={selectedTickets.length === tickets.length && tickets.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-[#ff9702] rounded"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticket Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Holder Information
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Purchase Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mb-2" />
                      <p className="text-gray-500">Loading tickets...</p>
                    </div>
                  </td>
                </tr>
              ) : filteredTickets.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <Ticket className="h-12 w-12 text-gray-300 mb-3" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
                      <p className="text-gray-500 mb-4">Try adjusting your search or generate new tickets</p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredTickets.map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <input
                        type="checkbox"
                        checked={selectedTickets.includes(ticket.id)}
                        onChange={() => handleSelectTicket(ticket.id)}
                        className="h-4 w-4 text-[#ff9702] rounded"
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <QrCode className="h-4 w-4 text-gray-400" />
                        <code className="font-mono text-sm font-medium text-gray-900">
                          {ticket.ticket_code}
                        </code>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{ticket.ticket_holder_name || 'Not assigned'}</div>
                        <div className="text-sm text-gray-500">{ticket.ticket_holder_email || '-'}</div>
                        {ticket.seat_number && (
                          <div className="text-xs text-gray-400 mt-1">
                            Seat: {ticket.seat_number} • Section: {ticket.section || 'General'}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <TicketStatusBadge status={ticket.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {ticket.ticket_type || 'General'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium">₵{ticket.price_paid || '0.00'}</div>
                        <div className="text-xs text-gray-400">
                          {ticket.created_at ? new Date(ticket.created_at).toLocaleDateString() : '-'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleViewTicket(ticket)}
                        className="text-blue-600 hover:text-blue-900 px-2 py-1 hover:bg-blue-50 rounded"
                      >
                        View
                      </button>
                      {ticket.status === 'purchased' && (
                        <button
                          onClick={() => handleRedeemConfirmation(ticket.ticket_code)}
                          className="text-green-600 hover:text-green-900 px-2 py-1 hover:bg-green-50 rounded"
                        >
                          Redeem
                        </button>
                      )}
                      <button
                        onClick={() => downloadTicketImage(ticket)}
                        className="text-purple-600 hover:text-purple-900 px-2 py-1 hover:bg-purple-50 rounded"
                      >
                        Download
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {filteredTickets.length > 0 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing <span className="font-medium">{filteredTickets.length}</span> of{' '}
                <span className="font-medium">{tickets.length}</span> tickets
              </div>
              <div className="flex items-center space-x-2">
                <button className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm bg-[#ff9702] text-white rounded-lg">1</span>
                <button className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ticket Preview Modal */}
      {showTicketModal && selectedTicket && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Ticket className="h-6 w-6 text-[#ff9702]" />
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">Ticket Preview</h3>
                  <p className="text-sm text-gray-500">{selectedTicket.ticket_code}</p>
                </div>
              </div>
              <button
                onClick={() => setShowTicketModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6">
              {/* Ticket Design Preview */}
              <div 
                ref={ticketRef}
                className="bg-gradient-to-br from-gray-50 to-white border-2 border-gray-300 rounded-xl p-6 shadow-xl max-w-2xl mx-auto mb-6"
              >
                {/* Ticket Header */}
                <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{eventDetails?.title || 'Event Name'}</h2>
                    <p className="text-gray-600">Ticket #{selectedTicket.ticket_code}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Status</div>
                    <TicketStatusBadge status={selectedTicket.status} />
                  </div>
                </div>
                
                {/* Ticket Body */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 mb-2">HOLDER INFORMATION</h3>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-gray-400" />
                          <span className="font-medium">{selectedTicket.ticket_holder_name || 'Not assigned'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Mail className="h-4 w-4 text-gray-400" />
                          <span className="text-sm">{selectedTicket.ticket_holder_email || '-'}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 mb-2">EVENT DETAILS</h3>
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span>{eventDetails?.formatted_date || 'Date'}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span>{eventDetails?.venue_name || 'Venue'}, {eventDetails?.city || 'City'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right Column */}
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-500 mb-2">TICKET INFORMATION</h3>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Type:</span>
                          <span className="font-medium">{selectedTicket.ticket_type || 'General'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Price:</span>
                          <span className="font-bold text-[#ff9702]">₵{selectedTicket.price_paid || '0.00'}</span>
                        </div>
                        {selectedTicket.seat_number && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Seat:</span>
                            <span className="font-medium">{selectedTicket.seat_number}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex justify-center pt-4">
                      <div className="text-center">
                        <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-2">
                          <QrCode className="h-16 w-16 text-gray-400" />
                        </div>
                        <p className="text-xs text-gray-500">Scan to verify</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Ticket Footer */}
                <div className="pt-6 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="text-xs text-gray-500">
                      Issued: {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleDateString() : '-'}
                    </div>
                    <div className="text-xs text-gray-500">
                      Valid for entry • Non-transferable
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={() => downloadTicketImage(selectedTicket)}
                  className="flex items-center space-x-2 bg-[#ff9702] text-white px-4 py-3 rounded-lg hover:bg-[#d18c13] transition-colors"
                >
                  <Download className="h-4 w-4" />
                  <span>Download as Image</span>
                </button>
                
                <button
                  onClick={() => window.print()}
                  className="flex items-center space-x-2 bg-gray-600 text-white px-4 py-3 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  <Printer className="h-4 w-4" />
                  <span>Print Ticket</span>
                </button>
                
                <button
                  onClick={() => handleRedeemConfirmation(selectedTicket.ticket_code)}
                  className="flex items-center space-x-2 bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors"
                >
                  <CheckCircle className="h-4 w-4" />
                  <span>Mark as Redeemed</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <div className="flex items-center space-x-3 mb-4">
              <AlertCircle className="h-6 w-6 text-yellow-500" />
              <h3 className="text-lg font-semibold text-gray-900">Confirm Action</h3>
            </div>
            <p className="text-gray-600 mb-6">{confirmMessage}</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  setPendingAction(null);
                  setConfirmMessage('');
                  setPendingTicketCode(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={executePendingAction}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTicketManager;